import * as React from 'react';
import { FlameGraphCustom } from './FlameGraphCustom.tsx';
import { ApiHelper } from '../ApiHelper';
import { MessageBar, MessageBarType, Link, Stack } from '@fluentui/react';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Dropdown, IDropdownOption, DropdownMenuItemType } from '@fluentui/react/lib/Dropdown';
import { Toggle } from '@fluentui/react/lib/Toggle';
import AutoSizer from 'react-virtualized-auto-sizer';
import { DataPoint } from '../utils/flamegraphLauncher.ts';
import { computeDiff, DiffResult, createMergedDiffTree } from '../utils/flamegraphDiff.ts';
import { DiffData } from './FlameGraphCustom.tsx';
import { formatMetricValue, isRawValueMetric } from '../utils/metricUtils.ts';

interface ProfileNode {
    name: string;
    value: number;
    exclusive: number;
    id: number;
    parentId: number;
    children?: ProfileNode[];
    tooltip?: string;
}

interface ProfileInfo {
    unfilteredTotal: number;
    nodes: ProfileNode[] | null;
    error: string | null;
}

interface ProfileStatsProps {
    profileInfo: ProfileInfo;
    currentNode: ProfileNode | null | undefined;
    metric: number;  // Metric ID for proper unit formatting
}

// Discriminated union for ProfilePane props
interface CommitProfileProps {
    mode: 'commit';
    commitId: string;
    serviceId: number | string;
    env: number;
    process: number;
    type: number;
    metric: number;
    date: string;
    isCommitData?: boolean;
    timeSeriesData?: DataPoint[];
    enableComparison?: boolean;
    onclose?: () => void;
}

interface ApiProfileProps {
    mode: 'apiprofile';
    leftPath: string;
    rightPath: string;
    streamName: string;
    frameName: string;
    metric: number;
    onclose: () => void;
}

type ProfilePaneProps = CommitProfileProps | ApiProfileProps;

export function ProfilePane(props: ProfilePaneProps) {
    // Extract common props
    const metric = props.metric;
    const mode = props.mode;

    const [profileInfo, setProfileInfo] = React.useState<ProfileInfo | undefined>(undefined);
    const [currentNode, setCurrentNode] = React.useState<ProfileNode | undefined>(undefined);

    const [loading, setLoading] = React.useState(true);
    const [loadingUntrimmed, setLoadingUntrimmed] = React.useState(false);

    // Diff mode state
    const [comparisonDate, setComparisonDate] = React.useState<string | null>(null);
    const [comparisonProfile, setComparisonProfile] = React.useState<ProfileInfo | undefined>(undefined);
    const [loadingComparison, setLoadingComparison] = React.useState(false);
    const [diffMode, setDiffMode] = React.useState<'overlay' | 'differential'>('overlay');
    const [showMethodFirst, setShowMethodFirst] = React.useState(true);

    // 3-way view mode (baseline/comparison/diff) - used for both commit and API profile modes when in comparison
    const [viewMode, setViewMode] = React.useState<'baseline' | 'comparison' | 'diff'>('diff');

    // Warning state for zoom stability messages
    const [zoomWarning, setZoomWarning] = React.useState<string | null>(null);

    // Load theme from localStorage, default to 'azure'
    const [theme, setTheme] = React.useState<'azure' | 'minimal' | 'highcontrast' | 'grayscale'>(() => {
        const savedTheme = localStorage.getItem('flamegraph-theme');
        return (savedTheme as any) || 'azure';
    });

    // Save theme to localStorage when it changes
    React.useEffect(() => {
        localStorage.setItem('flamegraph-theme', theme);
    }, [theme]);

    const lastProfileUrlRef = React.useRef<string | undefined>(undefined);
    const lastProfileDataRef = React.useRef<ProfileInfo | undefined>(undefined);

    // Extract stable primitives from props so effects don't re-fire when unrelated
    // props (e.g. timeSeriesData) change reference on parent re-renders.
    const commitProps = mode === 'commit' ? props as CommitProfileProps : undefined;
    const fetchCommitId = commitProps?.commitId;
    const fetchServiceId = commitProps?.serviceId;
    const fetchEnv = commitProps?.env;
    const fetchProcess = commitProps?.process;
    const fetchDate = commitProps?.date;
    const fetchIsCommitData = commitProps?.isCommitData;

    const apiProps = mode === 'apiprofile' ? props as ApiProfileProps : undefined;
    const fetchLeftPath = apiProps?.leftPath;
    const fetchRightPath = apiProps?.rightPath;
    const fetchStreamName = apiProps?.streamName;
    const fetchFrameName = apiProps?.frameName;

    // todo
    // warning about Waits %
    // prompt to click on icon
    // diff
    // mem leak
    // comments
    // set env on webapp
    // FAQ
    // searchable waits profiles
    // backfill
    // commit view state in url
    function onNodeChange(node) {
        setCurrentNode(node);
    }

    // API Profile Mode: Load both left and right profiles
    React.useEffect(() => {
        if (mode !== 'apiprofile') return;

        const apiProps = props as ApiProfileProps;
        let isMounted = true;
        setLoading(true);
        setProfileInfo(undefined);
        setComparisonProfile(undefined);

        const buildUrl = (path: string) =>
            `/api/apiprofiles/flamegraph?path=${encodeURIComponent(path)}`
            + `&streamName=${encodeURIComponent(apiProps.streamName)}`
            + `&frameName=${encodeURIComponent(apiProps.frameName)}`
            + `&trimmed=true`;

        Promise.all([
            ApiHelper<ProfileInfo>(buildUrl(apiProps.leftPath)),
            ApiHelper<ProfileInfo>(buildUrl(apiProps.rightPath))
        ]).then(([leftData, rightData]) => {
            if (!isMounted) return;

            // Post-process both profiles
            if (leftData && leftData.error == null) {
                postProcessProfileInfo(leftData, metric);
            }
            if (rightData && rightData.error == null) {
                postProcessProfileInfo(rightData, metric);
            }

            setProfileInfo(leftData);
            setComparisonProfile(rightData);
            setComparisonDate('comparison'); // Auto-enable diff mode
            setLoading(false);
        }).catch(err => {
            if (!isMounted) return;
            setProfileInfo({ unfilteredTotal: 0, nodes: null, error: err.message || 'Failed to load profiles' });
            setLoading(false);
        });

        return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props intentionally excluded; fetch is keyed on stable URL primitives
    }, [mode, fetchLeftPath, fetchRightPath, fetchStreamName, fetchFrameName, metric]);

    // Commit Mode: Load single profile with optional comparison
    React.useEffect(() => {
        if (mode !== 'commit') return;

        const commitProps = props as CommitProfileProps;
        let isMounted = true;
        setLoading(true);
        setLoadingUntrimmed(false);
        setProfileInfo(undefined);

        const fetchProfile = (trimmedValue: boolean, retryOnEmpty: boolean) => {
            const dateParts = commitProps.date.split('/');
            const functionId = dateParts.length > 1 && dateParts[1].length > 0 ? dateParts[1] : null;
            const leafName = dateParts.length > 2 ? dateParts[2] : null;

            const apiUrl = `/api/service/${commitProps.serviceId}/Commits/${commitProps.commitId}/flamegraph/${commitProps.process}/${metric}/${commitProps.env}?date=${dateParts[0]}`
                + (functionId != null ? '&functionid=' + functionId : '')
                + (leafName != null ? '&leafname=' + leafName : '')
                + (commitProps.isCommitData != null ? '&isCommitData=' + commitProps.isCommitData : '')
                + '&trimmed=' + trimmedValue;

            if (lastProfileUrlRef.current === apiUrl && lastProfileDataRef.current) {
                setProfileInfo(lastProfileDataRef.current);
                setLoading(false);
                setLoadingUntrimmed(false);
                return;
            }

            lastProfileUrlRef.current = apiUrl;

            ApiHelper<ProfileInfo>(apiUrl)
                .then(data => {
                    if (!isMounted) return;
                    if (data.error == null) {
                        postProcessProfileInfo(data, metric);
                        lastProfileDataRef.current = data;
                    }

                    // If trimmed and no data, retry with untrimmed
                    if (
                        trimmedValue &&
                        (
                            !data.nodes ||
                            data.nodes.length === 0 ||
                            data.unfilteredTotal === 0 ||
                            (data.nodes[0] && data.nodes[0].value === 0)
                        ) &&
                        retryOnEmpty
                    ) {
                        setLoadingUntrimmed(true);
                        // Fetch untrimmed
                        fetchProfile(false, false);
                        return;
                    }
                    setProfileInfo(data);
                    setLoading(false);
                    setLoadingUntrimmed(false);
                });
        };

        fetchProfile(true, true);

        return () => { isMounted = false; };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- props intentionally excluded; fetch is keyed on stable URL primitives
    }, [mode, fetchCommitId, fetchServiceId, fetchEnv, fetchProcess, fetchDate, fetchIsCommitData, metric]);

    // Load comparison profile when comparison date is selected (commit mode only)
    React.useEffect(() => {
        if (mode !== 'commit') return;

        if (!comparisonDate) {
            setComparisonProfile(undefined);
            return;
        }

        const commitProps = props as CommitProfileProps;
        let isMounted = true;
        setLoadingComparison(true);

        const fetchComparisonProfile = (trimmedValue: boolean, retryOnEmpty: boolean) => {
            const dateParts = comparisonDate.split('/');
            const functionId = dateParts.length > 1 && dateParts[1].length > 0 ? dateParts[1] : null;
            const leafName = dateParts.length > 2 ? dateParts[2] : null;

            // Find commitId for comparison date from timeSeriesData
            const comparisonPoint = commitProps.timeSeriesData?.find(dp => dp.originalDate === comparisonDate);
            const comparisonCommitId = comparisonPoint?.commitId || commitProps.commitId;

            const apiUrl = `/api/service/${commitProps.serviceId}/Commits/${comparisonCommitId}/flamegraph/${commitProps.process}/${metric}/${commitProps.env}?date=${dateParts[0]}`
                + (functionId != null ? '&functionid=' + functionId : '')
                + (leafName != null ? '&leafname=' + leafName : '')
                + (commitProps.isCommitData != null ? '&isCommitData=' + commitProps.isCommitData : '')
                + '&trimmed=' + trimmedValue;

            ApiHelper<ProfileInfo>(apiUrl)
                .then(data => {
                    if (!isMounted) return;
                    if (data.error == null) postProcessProfileInfo(data, metric);

                    // If trimmed and no data, retry with untrimmed
                    if (
                        trimmedValue &&
                        (
                            !data.nodes ||
                            data.nodes.length === 0 ||
                            data.unfilteredTotal === 0 ||
                            (data.nodes[0] && data.nodes[0].value === 0)
                        ) &&
                        retryOnEmpty
                    ) {
                        // Fetch untrimmed
                        fetchComparisonProfile(false, false);
                        return;
                    }

                    setComparisonProfile(data);
                    setLoadingComparison(false);
                })
                .catch(() => {
                    if (isMounted) {
                        setLoadingComparison(false);
                    }
                });
        };

        fetchComparisonProfile(true, true);

        return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props intentionally excluded; fetch is keyed on stable URL primitives
    }, [mode, comparisonDate, fetchCommitId, fetchServiceId, fetchEnv, fetchProcess, fetchIsCommitData, metric]);

    // Helper functions for path-based node matching
    const buildAncestorPath = React.useCallback((root: ProfileNode, targetId: number): string[] | null => {
        if (root.id === targetId) return [root.name];

        if (root.children) {
            for (const child of root.children) {
                const childPath = buildAncestorPath(child, targetId);
                if (childPath) {
                    return [root.name, ...childPath];
                }
            }
        }
        return null;
    }, []);

    const followPath = React.useCallback((root: ProfileNode, path: string[]): ProfileNode | null => {
        if (path.length === 0) return null;
        if (path.length === 1) {
            // Last element - should match current node
            return root.name === path[0] ? root : null;
        }

        // First element should match root
        if (root.name !== path[0]) return null;

        // Find next child in path
        const nextName = path[1];
        const nextChild = root.children?.find(c => c.name === nextName);
        if (!nextChild) return null;

        // Continue following path
        return followPath(nextChild, path.slice(1));
    }, []);

    // Maintain zoom stability when switching view modes
    const prevViewModeRef = React.useRef<'baseline' | 'comparison' | 'diff'>(viewMode);
    React.useEffect(() => {
        // Only act if viewMode actually changed
        if (prevViewModeRef.current === viewMode) return;

        const prevViewMode = prevViewModeRef.current;
        prevViewModeRef.current = viewMode;

        // Clear any existing warning
        setZoomWarning(null);

        // If not zoomed (at root or no node selected), nothing to do
        if (!currentNode) return;

        // Get the previous profile root based on previous view mode
        let prevRoot: ProfileNode | undefined;
        if (prevViewMode === 'comparison' && comparisonProfile?.nodes?.[0]) {
            prevRoot = comparisonProfile.nodes[0];
        } else {
            // baseline or diff both use profileInfo as base
            prevRoot = profileInfo?.nodes?.[0];
        }

        // Get the new profile root based on new view mode
        let newRoot: ProfileNode | undefined;
        if (viewMode === 'comparison' && comparisonProfile?.nodes?.[0]) {
            newRoot = comparisonProfile.nodes[0];
        } else {
            // baseline or diff both use profileInfo as base
            newRoot = profileInfo?.nodes?.[0];
        }

        // If we don't have both profiles loaded, can't maintain zoom
        if (!prevRoot || !newRoot) return;

        // If switching between baseline and diff (both use same profile), no adjustment needed
        if (prevRoot === newRoot) return;

        // If current node is already the root of the new profile, no adjustment needed
        if (currentNode.id === newRoot.id) return;

        // Build path from previous root to current zoomed node
        const ancestorPath = buildAncestorPath(prevRoot, currentNode.id);

        if (!ancestorPath) {
            // Couldn't build path (shouldn't happen but handle gracefully)
            setCurrentNode(undefined);
            setZoomWarning(`Unable to maintain zoom when switching to ${viewMode} view. Resetting to root.`);
            return;
        }

        // Try to follow same path in new profile
        const equivalentNode = followPath(newRoot, ancestorPath);

        if (equivalentNode) {
            // Found equivalent node - update currentNode
            setCurrentNode(equivalentNode);
        } else {
            // Path doesn't exist in new profile - reset zoom and warn
            setCurrentNode(undefined);
            const pathStr = ancestorPath.slice(1).join(' → '); // Skip root name
            setZoomWarning(`Zoomed path "${pathStr}" not found in ${viewMode} view. Resetting to root.`);
        }
    }, [viewMode, currentNode, profileInfo, comparisonProfile, buildAncestorPath, followPath]);

    // Auto-dismiss warning after 5 seconds
    React.useEffect(() => {
        if (zoomWarning) {
            const timer = setTimeout(() => setZoomWarning(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [zoomWarning]);

    // Compute diff when both profiles are loaded
    const diffData: DiffData | undefined = React.useMemo(() => {
        // Only show diff when in 'diff' view mode
        if (viewMode !== 'diff') {
            return undefined;
        }

        // Check if we have both profiles
        if (!profileInfo?.nodes?.[0] || !comparisonProfile?.nodes?.[0]) {
            return undefined;
        }

        // For commit mode, only compute diff if comparison date is selected
        if (mode === 'commit' && !comparisonDate) {
            return undefined;
        }

        // If zoomed in, calculate diff for the focused subtree
        let baselineNode = profileInfo.nodes[0];
        let comparisonNode = comparisonProfile.nodes[0];
        let zoomPath: string[] | null = null;

        if (currentNode) {
            // Build path from currentNode - try baseline first, then comparison
            // (currentNode could be from baseline, comparison, or previous merged tree)
            let ancestorPath = buildAncestorPath(profileInfo.nodes[0], currentNode.id);

            if (!ancestorPath && comparisonProfile?.nodes?.[0]) {
                // Not found in baseline, try comparison
                ancestorPath = buildAncestorPath(comparisonProfile.nodes[0], currentNode.id);
            }

            if (ancestorPath && ancestorPath.length > 1) {
                // Store path for later use
                zoomPath = ancestorPath;

                // Follow path in both trees for zoom
                const focusedBaseline = followPath(profileInfo.nodes[0], ancestorPath);
                const focusedComparison = followPath(comparisonProfile.nodes[0], ancestorPath);

                if (focusedBaseline && focusedComparison) {
                    baselineNode = focusedBaseline;
                    comparisonNode = focusedComparison;
                } else if (focusedBaseline) {
                    // Node only in baseline (removed node)
                    baselineNode = focusedBaseline;
                } else if (focusedComparison) {
                    // Node only in comparison (new node)
                    comparisonNode = focusedComparison;
                }
            }
        }

        // Ensure baseline is always the earlier profile (commit mode only)
        let shouldSwap = false;
        if (mode === 'commit') {
            const commitProps = props as CommitProfileProps;
            const currentDate = new Date(commitProps.date.split('/')[0]);
            const compDate = comparisonDate ? new Date(comparisonDate.split('/')[0]) : new Date();
            shouldSwap = currentDate > compDate;
        }
        // For API profile mode, left is always baseline, right is comparison

        const actualBaseline = shouldSwap ? comparisonNode : baselineNode;
        const actualComparison = shouldSwap ? baselineNode : comparisonNode;

        const diffResult: DiffResult = computeDiff(actualBaseline, actualComparison);

        // Create merged tree for diff visualization (includes new and removed nodes)
        const mergedTree = createMergedDiffTree(actualBaseline, actualComparison, diffResult);

        return {
            mode: diffMode,
            baseline: actualBaseline,
            comparison: actualComparison,
            diffNodes: diffResult.diffNodes,
            mergedTree: mergedTree,
            newNodes: diffResult.newNodes,
            removedNodes: diffResult.removedNodes,
            totalDelta: diffResult.totalDelta,
            totalPercentChange: diffResult.totalPercentChange
        };
    }, [mode, viewMode, profileInfo, comparisonProfile, diffMode, currentNode, comparisonDate, props, buildAncestorPath, followPath]);

    function dismissPanel() {
        window.g_tooltipSelect(null);

        if (mode === 'apiprofile') {
            (props as ApiProfileProps).onclose();
        } else if (mode === 'commit') {
            (props as CommitProfileProps).onclose?.();
        }
    }

    return (
        <Panel
            isLightDismiss
            isOpen={true}
            type={PanelType.extraLarge}
            onDismiss={dismissPanel}
            closeButtonAriaLabel="Close"
            styles={{
                main: {
                    border: '2px solid #e0e0e0',
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }
            }}
        >
            {(loadingUntrimmed
                ? (
                    <Stack horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 16 }} styles={{ root: { marginBottom: 12 } }}>
                        <Spinner size={SpinnerSize.large} label="Looking at a larger data set (untrimmed profile)..." />
                    </Stack>
                )
                : loading && (
                    <Stack horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 16 }} styles={{ root: { marginBottom: 12 } }}>
                        <Spinner size={SpinnerSize.large} label="Loading profile data. Please wait..." />
                    </Stack>
                )
            )}
            {profileInfo?.error && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={true}
                    styles={{ root: { marginBottom: 12, borderRadius: 8 } }}
                >
                    <strong>Unable to load profile data.</strong><br />
                    {profileInfo.error}<br />
                    Please check your network connection or try again later.
                </MessageBar>
            )}
            {profileInfo && !profileInfo.error && (
                <>
                    <ProfileStats
                        profileInfo={profileInfo}
                        currentNode={currentNode}
                        metric={metric}
                    />

                    {/* Flamegraph controls */}
                    {/* Zoom stability warning */}
                    {zoomWarning && (
                        <MessageBar
                            messageBarType={MessageBarType.warning}
                            onDismiss={() => setZoomWarning(null)}
                            dismissButtonAriaLabel="Close"
                            styles={{ root: { marginBottom: 12 } }}
                        >
                            {zoomWarning}
                        </MessageBar>
                    )}

                    {/* View mode switcher - shown for API profiles or commit mode with comparison */}
                    {(mode === 'apiprofile' || (mode === 'commit' && comparisonDate)) && (
                        <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: 12 } }}>
                            <span>
                                <strong
                                    style={{ cursor: 'pointer', textDecoration: viewMode === 'baseline' ? 'underline' : 'none' }}
                                    onClick={() => setViewMode('baseline')}
                                    title="Show baseline profile only"
                                >
                                    Baseline
                                </strong>: {mode === 'apiprofile'
                                    ? (props as ApiProfileProps).leftPath
                                    : getDateLabel((props as CommitProfileProps).date, comparisonDate!, true)}
                                {' • '}
                                <strong
                                    style={{ cursor: 'pointer', textDecoration: viewMode === 'comparison' ? 'underline' : 'none' }}
                                    onClick={() => setViewMode('comparison')}
                                    title="Show comparison profile only"
                                >
                                    Comparison
                                </strong>: {mode === 'apiprofile'
                                    ? (props as ApiProfileProps).rightPath
                                    : getDateLabel((props as CommitProfileProps).date, comparisonDate!, false)}
                                {' • '}
                                <strong
                                    style={{ cursor: 'pointer', textDecoration: viewMode === 'diff' ? 'underline' : 'none', color: viewMode === 'diff' ? '#0078d4' : 'inherit' }}
                                    onClick={() => setViewMode('diff')}
                                    title="Show comparison view with differences"
                                >
                                    Diff View
                                </strong>
                            </span>
                        </MessageBar>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <Toggle
                                label="Hide namespace"
                                inlineLabel
                                checked={showMethodFirst}
                                onChange={(ev, checked) => setShowMethodFirst(!!checked)}
                                styles={{ root: { marginBottom: 0 } }}
                            />

                            {/* Commit Mode: Comparison dropdown */}
                            {mode === 'commit' && (props as CommitProfileProps).enableComparison && (props as CommitProfileProps).timeSeriesData && (props as CommitProfileProps).timeSeriesData.length > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <label style={{ fontSize: 14, fontWeight: 600 }}>Compare to:</label>
                                    <Dropdown
                                        placeholder="Select a date to compare"
                                        selectedKey={comparisonDate}
                                        options={buildComparisonOptions((props as CommitProfileProps).timeSeriesData!, (props as CommitProfileProps).date, metric)}
                                        onChange={(ev, option) => setComparisonDate(option?.key as string || null)}
                                        styles={{ root: { minWidth: 200 } }}
                                    />
                                </div>
                            )}

                            {/* Display mode toggle (only shown in diff view when comparison is active) */}
                            {viewMode === 'diff' && (mode === 'apiprofile' || (mode === 'commit' && comparisonDate)) && (
                                <Toggle
                                    label="Display mode"
                                    inlineLabel
                                    onText="Overlay (color-coded)"
                                    offText="Differential (delta only)"
                                    checked={diffMode === 'overlay'}
                                    onChange={(ev, checked) => setDiffMode(checked ? 'overlay' : 'differential')}
                                    styles={{ root: { marginBottom: 0 } }}
                                />
                            )}
                        </Stack>

                        <Dropdown
                            label="Theme"
                            selectedKey={theme}
                            options={[
                                { key: 'azure', text: 'Azure Profiler' },
                                { key: 'minimal', text: 'Minimal' },
                                { key: 'highcontrast', text: 'High Contrast' },
                                { key: 'grayscale', text: 'Grayscale' }
                            ]}
                            onChange={(ev, option) => setTheme(option?.key as any)}
                            styles={{ root: { minWidth: 140 } }}
                        />
                    </div>

                    {loadingComparison && (
                        <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginBottom: 12 } }}>
                            Loading comparison profile...
                        </MessageBar>
                    )}

                    {profileInfo.nodes && profileInfo.nodes.length > 0 && profileInfo.unfilteredTotal > 0 && (() => {
                        // Select tree based on view mode
                        let displayNode: ProfileNode;
                        let focusedNodeForTree: ProfileNode | undefined = currentNode;

                        if (viewMode === 'comparison' && comparisonProfile?.nodes?.[0]) {
                            // Show comparison tree
                            displayNode = comparisonProfile.nodes[0];
                        } else if (viewMode === 'baseline') {
                            // Show baseline tree
                            displayNode = profileInfo.nodes[0];
                        } else if (viewMode === 'diff' && diffData?.mergedTree) {
                            // Show merged tree for diff visualization (includes new and removed nodes)
                            displayNode = diffData.mergedTree;

                            // If we have a focused node, find its equivalent in the merged tree by path
                            if (currentNode) {
                                // Try to build path from baseline or comparison
                                let ancestorPath = buildAncestorPath(profileInfo.nodes[0], currentNode.id);
                                if (!ancestorPath && comparisonProfile?.nodes?.[0]) {
                                    ancestorPath = buildAncestorPath(comparisonProfile.nodes[0], currentNode.id);
                                }

                                // Follow the same path in merged tree
                                if (ancestorPath) {
                                    const mergedFocusedNode = followPath(diffData.mergedTree, ancestorPath);
                                    focusedNodeForTree = mergedFocusedNode || undefined;
                                }
                            }
                        } else {
                            // Default to baseline
                            displayNode = profileInfo.nodes[0];
                        }

                        return (
                            <AutosizedFlameGraph
                                data={displayNode}
                                onChange={node => onNodeChange(node)}
                                focusedNode={focusedNodeForTree}
                                showMethodFirst={showMethodFirst}
                                diffData={diffData}
                                theme={theme}
                                metric={metric}
                            />
                        );
                    })()}
                </>
            )}
        </Panel>
    );
}

function AutosizedFlameGraph({ data, onChange, focusedNode, showMethodFirst, diffData, theme, metric }: {
    data: ProfileNode;
    onChange: (node: ProfileNode) => void;
    focusedNode?: ProfileNode;
    showMethodFirst: boolean;
    diffData?: DiffData;
    theme: 'azure' | 'minimal' | 'highcontrast' | 'grayscale';
    metric?: number;
}) {
    return (
        <div style={{ height: '80vh' }}>
            <AutoSizer>
                {({ height, width }) => (
                    <FlameGraphCustom
                        data={data}
                        height={height}
                        width={width}
                        onChange={onChange}
                        focusedNode={focusedNode}
                        showMethodFirst={showMethodFirst}
                        diffData={diffData}
                        theme={theme}
                        metric={metric}
                    />
                )}
            </AutoSizer>
        </div>
    );
}

function ProfileStats({ profileInfo, currentNode, metric }: ProfileStatsProps) {
    if (!profileInfo.nodes || profileInfo.nodes.length === 0 || profileInfo.unfilteredTotal === 0) {
        return null; // MessageBar is shown above
    }
    const totalNode = currentNode != null ? currentNode : profileInfo.nodes[0];
    const percent = totalNode.value * 100.0 / profileInfo.unfilteredTotal;

    if (percent === 0) {
        return (
            <MessageBar
                messageBarType={MessageBarType.warning}
                isMultiline={true}
                styles={{ root: { marginBottom: 12, borderRadius: 8 } }}
            >
                <strong>Data point is not available for view.</strong><br />
                Some data may be missing due to system aggregation limits. This often happens for data collected from machines with lower activity.<br />
                For a complete view, please use the <Link href="https://azprofilerclko.azurewebsites.net/ClickOnce/AzureProfilerViewer.application" target="_blank" underline>Azure Profiler App</Link>.
            </MessageBar>
        );
    }

    // Check if this metric should display raw values (not percentages)
    // Includes: Exceptions (count), ApiCpu (μs), ApiDuration (μs)
    const isRawValue = isRawValueMetric(metric);

    return (
        <>
            <div>
                {isRawValue ? (
                    <>
                        <span>{formatMetricValue(totalNode.value, metric, 'commit')} total</span>
                        {currentNode && (
                            <span>. {formatMetricValue(currentNode.value, metric, 'commit')} in selection</span>
                        )}
                    </>
                ) : (
                    <>
                        <span>{sigdig(percent)}% of total</span>
                        {currentNode && (
                            <span>. {sigdig(currentNode.value * 100.0 / profileInfo.nodes[0].value)}% of PR functions</span>
                        )}
                    </>
                )}
            </div>
            <div>
                <>
                    <span>Click on a frame to zoom in.</span>
                    {currentNode && <span> Click on a parent to zoom out.</span>}
                </>
            </div>
        </>
    );
}

function postProcessProfileInfo(profileInfo, metric) {
    const nodes = profileInfo.nodes;
    nodes[0].name = '';
    for (const n of nodes) {
        if (n.parentId >= 0) {
            if (nodes[n.parentId].children === undefined) {
                nodes[n.parentId].children = [];
            }
            nodes[n.parentId].children.push(n);
        }
    }
}

function sigdig(value) {
    const s = value.toString();
    let periodFound = false;
    for (var i = 0; i < s.length; i++) {
        switch (s.charAt(i)) {
            case '.':
                periodFound = true;
                break;
            case '0':
                break;
            default:
                if (periodFound) {
                    return s.substring(0, i + 3);
                }
        }
    }

    return s;
}

function buildComparisonOptions(timeSeriesData: DataPoint[], currentDate: string, metric: number): IDropdownOption[] {
    return timeSeriesData.map(dp => ({
        key: dp.originalDate,
        text: `${dp.x.toLocaleDateString()} - ${formatMetricValue(dp.y, metric, 'commit')} ${dp.originalDate === currentDate ? '(current)' : ''}`,
        disabled: dp.originalDate === currentDate,
        itemType: dp.originalDate === currentDate ? DropdownMenuItemType.Header : DropdownMenuItemType.Normal
    }));
}

/**
 * Get date label for baseline/comparison, ensuring baseline is always earlier
 */
function getDateLabel(currentDate: string, comparisonDate: string, isBaseline: boolean): string {
    // Parse dates (format: YYYY-MM-DD)
    const current = new Date(currentDate.split('/')[0]);
    const comparison = new Date(comparisonDate.split('/')[0]);

    // Determine which is earlier (baseline)
    const isCurrentEarlier = current <= comparison;

    // Return the appropriate date based on what's being requested
    const targetDate = isBaseline
        ? (isCurrentEarlier ? current : comparison)
        : (isCurrentEarlier ? comparison : current);

    return targetDate.toLocaleDateString();
}
